import React, { useState } from 'react'
import AppBar from '@material-ui/core/AppBar'
import { default as MaterialTabs } from '@material-ui/core/Tabs'
import Tab from '@material-ui/core/Tab'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import AddAddress from 'components/Modal/ProductFormModal/AddAddress/AddAddress'
import { StyledSplitTab, StyledSplitTabsWrapper, StyledTabsWrapper } from './ProductFormTabs.styled'
import SVG from 'react-inlinesvg'
import checkmarkImage from 'assets/images/icons/checkmark.svg'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { selectAddressFormData } from 'slices/addressFormSlice'
import ProductCheckout from '../ProductCheckout/ProductCheckout'
import IndexObjectDataForm from '../Index/IndexObjectDataForm/IndexObjectDataForm'
import MapOrReportObjectDataForm from '../MapOrReport/MapOrReportObjectDataForm/MapOrReportObjectDataForm'
import MapOrReportAddEquipmentForm from '../MapOrReport/MapOrReportAddEquipmentForm/MapOrReportAddEquipmentForm'
import ValueAddEquipmentForm from '../Value/ValueAddEquipmentForm/ValueAddEquipmentForm'
import { makeStyles } from '@material-ui/core'
import { ObjectType } from 'types/ApiResponse'
import ValueAddObjectDataForm from '../Value/ValueAddObjectDataForm/ValueAddObjectDataForm'
import ValueAddModernizationForm from '../Value/ValueAddModernizationForm/ValueAddModernizationForm'

function TabPanel({ children, value, index, ...other }: any) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  )
}

function a11yProps(index: any) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  }
}

const SplitTabIndicator = ({ subTab, segments }) => {
  return (
    <StyledSplitTabsWrapper>
      {Array.from({ length: segments }).map((_, i) => (
        <StyledSplitTab key={i} checked={subTab >= i} />
      ))}
    </StyledSplitTabsWrapper>
  )
}

const useStyles = makeStyles({
  tabsWithCustomIndicator: {
    '& .MuiTabs-indicator': {
      backgroundColor: 'white',
    },
  },
})

export interface ProductFormTabsProps {
  onBackdropClick: () => void
}

const ProductFormTabs = (props: ProductFormTabsProps) => {
  const { onBackdropClick } = props
  const { t } = useTranslation()
  const addressFormData = useSelector(selectAddressFormData)
  const [tabIndex, setTabIndex] = useState(addressFormData.isEdit ? 1 : 0)
  const [objectTabIndex, setObjectTabIndex] = useState(0)
  const [checkoutTabIndex, setCheckoutTabIndex] = useState(0)
  const [isProductGenerated, setProductGenerated] = useState(false)
  const isIndex = addressFormData.productName === 'index'
  const isMapOrReport =
    addressFormData.productName === 'report' || addressFormData.productName === 'map'
  const isValue = addressFormData.productName === 'value'
  const classes = useStyles()
  const objectType = addressFormData.addressData?.propertyDetails?.objectType
  const isValueObjectSplitTab = isValue && tabIndex === 1 && objectType !== ObjectType.PLOT_OF_LAND
  const isCheckoutSplitTab =
    (isIndex && tabIndex === 2) || (isMapOrReport && tabIndex === 3) || (isValue && tabIndex === 4)

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTabIndex(newValue)
  }

  const isModernizationDisabled =
    (isValue && objectType === ObjectType.PLOT_OF_LAND) ||
    (isValue &&
      addressFormData.addressData?.propertyDetails?.constructionYear >
        new Date().getFullYear() - 10)

  return (
    <StyledTabsWrapper shouldNotTransition={isValueObjectSplitTab}>
      <AppBar position="relative">
        <MaterialTabs
          value={tabIndex}
          onChange={handleChange}
          aria-label="simple tabs example"
          variant="fullWidth"
          scrollButtons="off"
          className={
            isValueObjectSplitTab || isCheckoutSplitTab
              ? classes.tabsWithCustomIndicator
              : undefined
          }
          TabIndicatorProps={{
            children:
              isValueObjectSplitTab || isCheckoutSplitTab ? (
                <SplitTabIndicator
                  subTab={isValueObjectSplitTab ? objectTabIndex : checkoutTabIndex}
                  segments={isValueObjectSplitTab ? 4 : 2}
                />
              ) : null,
          }}
        >
          <Tab
            label={t('newAddressModal.addressTab.label')}
            disabled
            {...a11yProps(0)}
            icon={tabIndex > 0 && <SVG src={checkmarkImage} />}
          />
          <Tab
            label={t('newAddressModal.objectDataTab.label')}
            {...a11yProps(1)}
            disabled
            icon={tabIndex > 1 && <SVG src={checkmarkImage} />}
          />
          {!isIndex && (
            <Tab
              label={t('newAddressModal.furnishingTab.label')}
              {...a11yProps(2)}
              disabled
              className={isValue && objectType === ObjectType.PLOT_OF_LAND ? 'inactive' : ''}
              icon={
                tabIndex > 2 &&
                !(isValue && objectType === ObjectType.PLOT_OF_LAND) && <SVG src={checkmarkImage} />
              }
            />
          )}
          {isValue && (
            <Tab
              label={t('newAddressModal.modernizationTab.label')}
              {...a11yProps(3)}
              disabled
              className={isModernizationDisabled ? 'inactive' : ''}
              icon={tabIndex > 3 && !isModernizationDisabled && <SVG src={checkmarkImage} />}
            />
          )}
          <Tab
            label={t('newAddressModal.finishTab.label')}
            {...a11yProps(!isIndex ? (isValue ? 4 : 3) : 2)}
            disabled
            icon={
              tabIndex >= (!isIndex ? 3 : 2) && isProductGenerated && <SVG src={checkmarkImage} />
            }
          />
        </MaterialTabs>
      </AppBar>
      <TabPanel value={tabIndex} index={0}>
        <AddAddress setCurrentTab={setTabIndex} />
      </TabPanel>
      <TabPanel value={tabIndex} index={1}>
        {isIndex && <IndexObjectDataForm setCurrentTab={setTabIndex} />}
        {isMapOrReport && <MapOrReportObjectDataForm setCurrentTab={setTabIndex} />}
        {isValue && (
          <ValueAddObjectDataForm
            setCurrentTab={setTabIndex}
            setCurrentSubTab={setObjectTabIndex}
            currentSubTab={objectTabIndex}
          />
        )}
      </TabPanel>
      {!isIndex && (
        <TabPanel value={tabIndex} index={2}>
          {isMapOrReport && <MapOrReportAddEquipmentForm setCurrentTab={setTabIndex} />}
          {isValue && (
            <ValueAddEquipmentForm
              setCurrentTab={setTabIndex}
              isModernizationDisabled={isModernizationDisabled}
            />
          )}
        </TabPanel>
      )}
      {isValue && (
        <TabPanel value={tabIndex} index={3}>
          <ValueAddModernizationForm setCurrentTab={setTabIndex} />
        </TabPanel>
      )}
      <TabPanel value={tabIndex} index={!isIndex ? (isValue ? 4 : 3) : 2}>
        <ProductCheckout
          isReportGenerated={isProductGenerated}
          setReportGenerated={setProductGenerated}
          onBackdropClick={onBackdropClick}
          setCurrentTab={setTabIndex}
          setCurrentSubTab={setCheckoutTabIndex}
          currentSubTab={checkoutTabIndex}
          isModernizationDisabled={isModernizationDisabled}
        />
      </TabPanel>
    </StyledTabsWrapper>
  )
}

export default ProductFormTabs
